"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Settings, Building2, GraduationCap, ArrowRight, Crosshair, Hammer, Target, Brain } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const FOCUS = [
  { id: "execution", icon: Target, label: "The Discipline of Execution", body: "Excellence as an engineered system. The mechanisms — protocols, checklists, reviews, outcome accountability — that convert ambition into verified results.", href: "/speaking/how-excellence-actually-runs/", gradient: "from-red/15 to-red/0", accent: "text-red", iconBg: "bg-gradient-sovereign" },
  { id: "diagnosis", icon: Brain, label: "Leadership as Diagnosis", body: "The clinical method applied to organizational leadership. Triage, differential diagnosis, protocol, judgment, outcome ownership, and the discipline of doing no harm under pressure.", href: "/speaking/leadership-as-diagnosis/", gradient: "from-purple/15 to-purple/0", accent: "text-purple", iconBg: "bg-gradient-royal" },
  { id: "70percent", icon: Crosshair, label: "The 70% Project", body: "The operating case for women in operational leadership. Not a diversity initiative — a talent strategy with a decade of evidence.", href: "/the-70-percent-project/", gradient: "from-amber-gold/15 to-amber-gold/0", accent: "text-amber-gold", iconBg: "bg-gradient-premium" },
  { id: "building", icon: Hammer, label: "Building Things That Last", body: "Entrepreneurship, governance delivery, and the discipline of constructing consequential organizations in complex environments. A decade of co-building a Digital Conglomerate reveals what endures.", href: "/speaking/build-in-the-dark/", gradient: "from-emerald/15 to-emerald/0", accent: "text-emerald", iconBg: "bg-gradient-success" },
  { id: "governance", icon: Building2, label: "Governance & Public Policy", body: "Digital governance transformation, citizen engagement, and policy implementation. The capability to convert political authority into measurable public outcomes.", href: "/expertise/governance-public-policy/", gradient: "from-denim/15 to-denim/0", accent: "text-denim", iconBg: "bg-gradient-depth" },
  { id: "intelligence", icon: Settings, label: "Strategic Intelligence", body: "Real-time intelligence gathering, predictive analytics, and decision-ready information derivation. The capability to see what others cannot — and to act on it before competitors detect the signal.", href: "/expertise/strategic-intelligence/", gradient: "from-cyan/15 to-cyan/0", accent: "text-cyan", iconBg: "bg-gradient-clarity" },
];

export function FocusAreasSection() {
  return (
    <Section id="focus-areas" ariaLabel="Areas of Focus" background="default">
      <SectionHeader
        icon={Crosshair}
        overline="Areas of Focus"
        overlineClassName="text-purple"
        pillClassName="glass-tinted-purple border-purple/20"
        title={
          <>
            Where the work is <span className="text-gradient-royal">concentrated.</span>
          </>
        }
        subtitle="Six areas of focus, each grounded in a decade of sovereign-scale operations and verified outcomes."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {FOCUS.map((f, i) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
          >
            <Link href={f.href} className="block h-full group">
              <div className={`relative glass-card rounded-2xl p-6 h-full overflow-hidden bg-gradient-to-br ${f.gradient} to-transparent border border-border/30 group-hover:scale-[1.01] transition-all`}>
                <div aria-hidden className="absolute -top-12 -right-12 size-40 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl ${f.iconBg} flex items-center justify-center shadow-lg mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <f.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${f.accent}`}>{f.label}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{f.body}</p>
                  <div className={`inline-flex items-center gap-1.5 text-body-sm font-semibold ${f.accent} group-hover:gap-2.5 transition-all`}>
                    Explore <ArrowRight className="size-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
